import { uniqueId } from 'lodash';

import { type BriefType, contentTypeFromBriefType } from 'src/data/brief';
import { CONTENT_TYPE } from 'src/data/contentType';
import { GENERATION_GUIDANCE_TYPE, type GenerationGuidanceType } from 'src/data/generation/const';
import { type GenerationTool, getToolMaxWordsPerKeyword } from 'src/data/generationTool';
import { SUGGESTED_ANNOTATIONS_ORIGIN } from 'src/data/performance';
import { countWords } from 'src/lib/string';

import { TEXT_ANNOTATION_TYPE } from './const';
import type {
  MappedExtractedAnnotation,
  MappedPerformanceCenterAnnotation,
  TalkingPointsView,
  TextAnnotation,
  TextAnnotationMap,
  TextAnnotationsItem,
} from './types';

const EMPTY_ANNOTATIONS: TextAnnotationsItem[] = Object.values(TEXT_ANNOTATION_TYPE).map(
  (type) => ({
    type,
    value: [],
  })
);

const annotationsMapToArray = (annotationMap: TextAnnotationMap): TextAnnotationsItem[] => {
  if (!annotationMap) {
    return [];
  }

  // Display only supported types in correct order
  const array = Object.values(TEXT_ANNOTATION_TYPE).map((type) => ({
    type,
    value: annotationMap[type],
  }));

  // Display only non-empty values
  return array.filter(({ value }) => Array.isArray(value) && value.length > 0);
};

const withoutMainOffering = (entry: TextAnnotationsItem): boolean =>
  entry.type !== TEXT_ANNOTATION_TYPE.mainOfferings;

const filterAnnotations = (annotations: TextAnnotationsItem[], briefType: BriefType) => {
  // For "content" briefs we should not display "main offering"
  if (contentTypeFromBriefType(briefType) === CONTENT_TYPE.content) {
    return annotations.filter(withoutMainOffering);
  }
  return annotations;
};

export const prepareAnnotations = (
  annotationMap: TextAnnotationMap,
  { briefType, loading }: { briefType: BriefType; loading: boolean }
): TextAnnotationsItem[] => {
  const annotations = annotationsMapToArray(annotationMap || {});
  const arrayToFilter = loading ? EMPTY_ANNOTATIONS : annotations;
  return filterAnnotations(arrayToFilter, briefType);
};

const FIELD_TO_TYPE_MAP = {
  features: GENERATION_GUIDANCE_TYPE.feature,
  benefits: GENERATION_GUIDANCE_TYPE.benefit,
  pains: GENERATION_GUIDANCE_TYPE.pain,
  keywords: GENERATION_GUIDANCE_TYPE.keyword,
  homePage: GENERATION_GUIDANCE_TYPE.home_page,
  landingPage: GENERATION_GUIDANCE_TYPE.landing_page,
  competitors: GENERATION_GUIDANCE_TYPE.competitors,
  thoughtLeadership: GENERATION_GUIDANCE_TYPE.thoughtLeadership,
  productPage: GENERATION_GUIDANCE_TYPE.productPage,
  other: GENERATION_GUIDANCE_TYPE.other,
  reviewsAmazon: GENERATION_GUIDANCE_TYPE.amazon,
  reviewsTrustpilot: GENERATION_GUIDANCE_TYPE.trustpilot,
  hubspotEmails: GENERATION_GUIDANCE_TYPE.hubspot,
  google: GENERATION_GUIDANCE_TYPE.google,
  facebook: GENERATION_GUIDANCE_TYPE.facebook,
  linkedin: GENERATION_GUIDANCE_TYPE.linkedin,
  facebookPages: GENERATION_GUIDANCE_TYPE.facebookPages,
  instagramAccount: GENERATION_GUIDANCE_TYPE.instagram,
  linkedinSocial: GENERATION_GUIDANCE_TYPE.linkedinSocial,
  genericResource: GENERATION_GUIDANCE_TYPE.genericResource,
};

export const onlyScoredAnnotationsByType = (
  annotations: MappedExtractedAnnotation[],
  type: GenerationGuidanceType
) => {
  return annotations
    .filter((annotation) => annotation.type === type)
    .filter((annotation) => annotation.score > 0);
};

export const convertExtractedAnnotations = (
  queryResult: TextAnnotationMap
): MappedExtractedAnnotation[] => {
  return Object.entries(FIELD_TO_TYPE_MAP)
    .map(([field, annotationType]) => {
      const result = queryResult?.[field] || [];
      return result.map(({ name, score }) => ({
        id: uniqueId(annotationType),
        type: annotationType,
        content: name,
        score,
        origin: SUGGESTED_ANNOTATIONS_ORIGIN.anyword,
      }));
    })
    .reduce((acc, items) => [...acc, ...items], []);
};

export const convertExtractedPerformanceCenterAnnotations = (tpView: TalkingPointsView) => {
  return {
    ...tpView,
    talkingPoints: tpView.talkingPoints?.map(
      ({ rating, talkingPoint }): MappedPerformanceCenterAnnotation => ({
        id: uniqueId(),
        content: talkingPoint,
        score: rating,
        accountId: tpView?.id,
        accountName: tpView?.name,
        accountUrl: tpView?.accountUrl,
        dataSource: tpView.dataSource,
      })
    ),
  };
};

export const filterAnnotationsByTool = (
  annotations: TextAnnotation[],
  generationTool: GenerationTool
) => {
  const maxWords = getToolMaxWordsPerKeyword(generationTool) || 0;
  return annotations.filter(({ name }) => countWords(name) <= maxWords);
};

export const isNotTalkingPointAnnotation = (
  annotations: { type: GenerationGuidanceType }[]
): { type: GenerationGuidanceType }[] => {
  const talkingPointTypes: GenerationGuidanceType[] = [
    GENERATION_GUIDANCE_TYPE.feature,
    GENERATION_GUIDANCE_TYPE.google,
    GENERATION_GUIDANCE_TYPE.facebook,
    GENERATION_GUIDANCE_TYPE.linkedin,
    GENERATION_GUIDANCE_TYPE.keyword,
    GENERATION_GUIDANCE_TYPE.amazon,
    GENERATION_GUIDANCE_TYPE.trustpilot,
    GENERATION_GUIDANCE_TYPE.home_page,
    GENERATION_GUIDANCE_TYPE.landing_page,
    GENERATION_GUIDANCE_TYPE.competitors,
    GENERATION_GUIDANCE_TYPE.thoughtLeadership,
    GENERATION_GUIDANCE_TYPE.productPage,
    GENERATION_GUIDANCE_TYPE.other,
    GENERATION_GUIDANCE_TYPE.hubspot,
    GENERATION_GUIDANCE_TYPE.facebookPages,
    GENERATION_GUIDANCE_TYPE.instagram,
    GENERATION_GUIDANCE_TYPE.linkedinSocial,
    GENERATION_GUIDANCE_TYPE.genericResource,
  ];
  return annotations.filter((annotation) => !talkingPointTypes.includes(annotation.type));
};
